import { useEffect, useMemo, useState } from 'react';
import type { PropiedadResumen, Semaforo, UsuarioSesion } from '@matias-asset-os/contracts';
import { t, type Idioma } from '@matias-asset-os/i18n';
import { api } from '../api.js';
import { ChipNio, CLASE_SEMAFORO, COLOR_SEMAFORO, FotoVilla, semillaDe } from '../componentes.js';
import { Icono } from '../componentes/Iconos.js';

interface Props {
  usuario: UsuarioSesion;
  idioma: Idioma;
  onAbrir: (id: string) => void;
  onNueva: () => void;
  onPartes: () => void;
}

type Vista = 'tarjetas' | 'lista';
type Orden = 'estado' | 'nombre' | 'codigo';

const PESO_SEMAFORO: Record<Semaforo, number> = { ROJO: 0, AMARILLO: 1, VERDE: 2 };

/**
 * Cartera de propiedades.
 *
 * Dos vistas, como en la aplicación de referencia: tarjetas (por defecto) y
 * lista. La tarjeta lleva la pastilla de estado operativo y la de nivel
 * superpuestas sobre la ilustración, el código abajo, y en el pie el titular y
 * el conteo de incidencias abiertas. Todo sale del backend real; la imagen es
 * una ilustración marcada como ficticia mientras no haya fotos reales.
 */
export function Propiedades({ usuario, idioma, onAbrir, onNueva, onPartes }: Props) {
  const [propiedades, setPropiedades] = useState<PropiedadResumen[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [vista, setVista] = useState<Vista>('tarjetas');
  const [orden, setOrden] = useState<Orden>('estado');
  const [busqueda, setBusqueda] = useState('');
  const esInterno = usuario.rol === 'DIRECTOR' || usuario.rol === 'MANAGER';
  const esDireccion = usuario.rol === 'DIRECTOR';

  useEffect(() => {
    api
      .propiedades()
      .then(({ propiedades: lista }) => setPropiedades(lista))
      .catch(() => setError(t(idioma, 'error.servidor')));
  }, [idioma]);

  const filtradas = useMemo(() => {
    if (!propiedades) return [];
    const texto = busqueda.trim().toLowerCase();
    const lista = propiedades.filter((p) =>
      texto === ''
        ? true
        : `${p.codigo} ${p.nombre} ${p.zona ?? ''}`.toLowerCase().includes(texto),
    );
    return [...lista].sort((a, b) => {
      if (orden === 'nombre') return a.nombre.localeCompare(b.nombre);
      if (orden === 'codigo') return a.codigo.localeCompare(b.codigo);
      const dif = PESO_SEMAFORO[a.semaforo] - PESO_SEMAFORO[b.semaforo];
      return dif !== 0 ? dif : a.nombre.localeCompare(b.nombre);
    });
  }, [propiedades, busqueda, orden]);

  const total = propiedades?.length ?? 0;
  const atencion = (propiedades ?? []).filter((p) => p.semaforo !== 'VERDE').length;
  const activas = (propiedades ?? []).filter((p) => p.estado === 'ACTIVE').length;

  return (
    <main>
      <div className="cabecera-seccion">
        <div>
          <h1>{t(idioma, 'propiedades.titulo')}</h1>
          {/*
            Para un manager la cifra no es «la cartera»: son las propiedades que
            LE han asignado. Decir «en total» le haria creer que ve todo.
          */}
          <p>
            {t(idioma, esDireccion ? 'cartera.resumen' : 'cartera.resumenAsignadas', {
              total,
              atencion,
            })}
          </p>
        </div>
        <div className="acciones">
          <select
            aria-label={t(idioma, 'cartera.ordenar')}
            value={orden}
            onChange={(e) => setOrden(e.target.value as Orden)}
          >
            <option value="estado">{t(idioma, 'cartera.ordenEstado')}</option>
            <option value="nombre">{t(idioma, 'cartera.ordenNombre')}</option>
            <option value="codigo">{t(idioma, 'cartera.ordenCodigo')}</option>
          </select>

          <div className="conmutador-vista" role="group" aria-label={t(idioma, 'propiedades.titulo')}>
            <button
              type="button"
              aria-pressed={vista === 'lista'}
              onClick={() => setVista('lista')}
              title={t(idioma, 'cartera.verLista')}
              aria-label={t(idioma, 'cartera.verLista')}
            >
              ☰
            </button>
            <button
              type="button"
              aria-pressed={vista === 'tarjetas'}
              onClick={() => setVista('tarjetas')}
              title={t(idioma, 'cartera.verTarjetas')}
              aria-label={t(idioma, 'cartera.verTarjetas')}
            >
              ▤
            </button>
          </div>

          {esDireccion && (
            <>
              <button type="button" className="secundario" onClick={onPartes}>
                {t(idioma, 'party.titulo')}
              </button>
              <button type="button" onClick={onNueva}>
                + {t(idioma, 'cartera.nueva')}
              </button>
            </>
          )}
        </div>
      </div>

      {error && (
        <p className="error" role="alert">
          {error}
        </p>
      )}

      {propiedades === null && !error && <p>{t(idioma, 'app.cargando')}</p>}

      {propiedades !== null && total === 0 && (
        <div className="tarjeta">
          <p>{t(idioma, 'propiedades.vacio')}</p>
        </div>
      )}

      {total > 0 && (
        <input
          type="search"
          value={busqueda}
          onChange={(e) => setBusqueda(e.target.value)}
          placeholder={t(idioma, 'cartera.buscar')}
          aria-label={t(idioma, 'cartera.buscar')}
          style={{ maxWidth: '24rem', marginBottom: '1rem' }}
        />
      )}

      {total > 0 && filtradas.length === 0 && (
        <div className="tarjeta">
          <p>{t(idioma, 'cartera.sinResultados')}</p>
          <button
            type="button"
            className="secundario"
            style={{ alignSelf: 'flex-start' }}
            onClick={() => setBusqueda('')}
          >
            {t(idioma, 'cartera.limpiar')}
          </button>
        </div>
      )}

      {filtradas.length > 0 && vista === 'tarjetas' && (
        <div className="rejilla villas">
          {filtradas.map((p) => (
            <TarjetaVilla key={p.id} propiedad={p} idioma={idioma} onAbrir={() => onAbrir(p.id)} />
          ))}
        </div>
      )}

      {filtradas.length > 0 && vista === 'lista' && (
        <div className="tarjeta tabla-scroll">
          <table>
            <thead>
              <tr>
                <th scope="col">{t(idioma, 'propiedades.codigo')}</th>
                <th scope="col">{t(idioma, 'propiedades.nombre')}</th>
                <th scope="col">{t(idioma, 'propiedades.propietarios')}</th>
                <th scope="col">{t(idioma, 'propiedades.semaforo')}</th>
                <th scope="col" title={t(idioma, 'nio.explicacion')}>
                  {t(idioma, 'propiedades.nio')}
                </th>
                <th scope="col" className="num">
                  {t(idioma, 'propiedades.incidencias')}
                </th>
                {esInterno && <th scope="col">{t(idioma, 'propiedades.requisitos')}</th>}
              </tr>
            </thead>
            <tbody>
              {filtradas.map((p) => (
                <tr key={p.id}>
                  <td>
                    <button type="button" className="enlace" onClick={() => onAbrir(p.id)}>
                      {p.codigo}
                    </button>
                  </td>
                  <td>
                    {p.nombre}
                    {p.zona ? <span className="suave"> · {p.zona}</span> : null}
                  </td>
                  <td>
                    {p.propietarios.length === 0 ? (
                      <span className="suave">{t(idioma, 'cartera.sinTitular')}</span>
                    ) : (
                      p.propietarios.map((o) => o.nombre).join(', ')
                    )}
                  </td>
                  <td>
                    <span className={`pastilla ${CLASE_SEMAFORO[p.semaforo]}`}>
                      {t(idioma, `semaforo.${p.semaforo}`)}
                    </span>
                  </td>
                  <td>
                    <ChipNio nivel={p.nivelNio} />
                  </td>
                  <td className="num">
                    {p.incidenciasAbiertas > 0 ? (
                      <b style={{ color: 'var(--ambar)' }}>{p.incidenciasAbiertas}</b>
                    ) : (
                      <span className="suave">—</span>
                    )}
                  </td>
                  {esInterno && (
                    <td>
                      {p.requisitosPendientes === 0 ? (
                        <span className="pastilla verde">
                          {t(idioma, 'propiedades.sinRequisitos')}
                        </span>
                      ) : (
                        <span className="pastilla ambar">{p.requisitosPendientes}</span>
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
          <div className="pie-tabla">
            <div className="leyenda">
              {(['VERDE', 'AMARILLO', 'ROJO'] as const).map((s) => (
                <span key={s}>
                  <i style={{ background: COLOR_SEMAFORO[s] }} />
                  {(propiedades ?? []).filter((p) => p.semaforo === s).length}{' '}
                  {t(idioma, `semaforo.${s}`)}
                </span>
              ))}
            </div>
            <span className="numerico">{t(idioma, 'cartera.activas', { total: activas })}</span>
          </div>
        </div>
      )}
    </main>
  );
}

function TarjetaVilla({
  propiedad,
  idioma,
  onAbrir,
}: {
  propiedad: PropiedadResumen;
  idioma: Idioma;
  onAbrir: () => void;
}) {
  const titular =
    propiedad.propietarios.length > 0
      ? propiedad.propietarios.map((o) => o.nombre).join(', ')
      : t(idioma, 'cartera.sinTitular');

  return (
    <button type="button" className="villa" onClick={onAbrir}>
      <span className="lienzo">
        {/*
          La foto real manda. La ilustracion ficticia es el respaldo de una casa
          que todavia no tiene ninguna, no la portada por defecto de todas.
        */}
        {propiedad.fotoPortadaUrl ? (
          <img className="foto-portada" src={propiedad.fotoPortadaUrl} alt="" />
        ) : (
          <FotoVilla semilla={semillaDe(propiedad.id)} alto={170} />
        )}
        <span className="encima">
          <span className="sobre-foto">
            <i style={{ background: COLOR_SEMAFORO[propiedad.semaforo] }} />
            {t(idioma, `semaforo.${propiedad.semaforo}`)}
          </span>
          <ChipNio nivel={propiedad.nivelNio} sobreFoto />
        </span>
        <span className="codigo-foto">{propiedad.codigo}</span>
      </span>

      <span className="cuerpo">
        <span className="titulo">
          <b>{propiedad.nombre}</b>
          <span aria-hidden="true" className="suave">
            ›
          </span>
        </span>
        <span className="lugar">
          <Icono nombre="pin" />
          {propiedad.zona ?? propiedad.pais}
        </span>
      </span>

      <span className="pie">
        <span className="suave" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {titular}
        </span>
        {propiedad.incidenciasAbiertas > 0 && (
          <span className="conteo" title={t(idioma, 'cartera.incidenciasAbiertas')}>
            <span className="aviso">
                  <Icono nombre="aviso" /> {propiedad.incidenciasAbiertas}
                </span>
            {/*
              Los niveles que hay, sin contarlos. Cuatro incidencias no dicen
              nada; cuatro incidencias de las que una es N1 lo dicen todo.
            */}
            <span className="niveles-abiertos" aria-hidden="true">
              {/*
                Con `?? []` a proposito: durante un despliegue la web puede
                cargarse unos segundos antes que la API, y una lista de casas en
                blanco por un campo que aun no llega es peor que unas pastillas
                que tardan un momento en aparecer.
              */}
              {(propiedad.nivelesAbiertos ?? []).map((n) => (
                <span key={n} className={`chip-nivel n${n}`}>
                  N{n}
                </span>
              ))}
            </span>
          </span>
        )}
      </span>
    </button>
  );
}
